import { useState, useEffect, useCallback, useRef } from 'react'
import {
  onboardingControllerFindOrCreateTutorialEndpoint,
  onboardingControllerGetChecklistStatus,
  type ChecklistStatusResponseDto
} from 'api/onboarding-custom'
import { TutorialResponseDtoState } from 'api/generated/schemas'
import { useUserInfo } from 'utils/hooks/use-userInfo'

type UseOnboardingChecklistReturn = {
  checklistStatus: ChecklistStatusResponseDto | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  shouldShowChecklist: boolean
}

export const useOnboardingChecklist = (): UseOnboardingChecklistReturn => {
  const { user, loading: userLoading } = useUserInfo()

  const [checklistStatus, setChecklistStatus] =
    useState<ChecklistStatusResponseDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [shouldShowChecklist, setShouldShowChecklist] = useState(false)
  const isFetchingRef = useRef(false)
  const lastReaderIdRef = useRef<string | number | null>(null)

  const fetchChecklistStatus = useCallback(async () => {
    if (userLoading || !user?.reader?.id) {
      return
    }

    const readerId = user.reader.id
    const userId =
      typeof readerId === 'string' ? parseInt(readerId, 10) : readerId

    if (isFetchingRef.current || readerId === lastReaderIdRef.current) {
      return
    }

    const hasFeatureFlag = user.featureToggles?.onboardingAlunoV3 === true
    const canAccessOnboarding = user.reader?.canAccessOnboarding === true

    if (!hasFeatureFlag || !canAccessOnboarding) {
      setShouldShowChecklist(false)
      return
    }

    isFetchingRef.current = true
    lastReaderIdRef.current = readerId
    setIsLoading(true)
    setError(null)

    try {
      const tutorialResponse =
        await onboardingControllerFindOrCreateTutorialEndpoint({
          name: 'checklist_onboarding'
        })

      if (tutorialResponse.status === 400 || !tutorialResponse.data) {
        setShouldShowChecklist(false)
        return
      }

      if (tutorialResponse.data.state === TutorialResponseDtoState.completed) {
        setShouldShowChecklist(false)
        return
      }

      setShouldShowChecklist(true)
      const response = await onboardingControllerGetChecklistStatus(userId)
      setChecklistStatus((response?.data as ChecklistStatusResponseDto) ?? null)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch checklist status')
      )
    } finally {
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }, [
    userLoading,
    user?.reader?.id,
    user?.featureToggles?.onboardingAlunoV3,
    user?.reader?.canAccessOnboarding
  ])

  useEffect(() => {
    if (!userLoading && user?.reader?.id) {
      fetchChecklistStatus()
    }
  }, [userLoading, user?.reader?.id, fetchChecklistStatus])

  useEffect(() => {
    const handleOnboardingCompleted = () => {
      lastReaderIdRef.current = null
      fetchChecklistStatus()
    }

    window.addEventListener('onboarding-completed', handleOnboardingCompleted)

    return () => {
      window.removeEventListener(
        'onboarding-completed',
        handleOnboardingCompleted
      )
    }
  }, [fetchChecklistStatus])

  const refetch = useCallback(async () => {
    lastReaderIdRef.current = null
    await fetchChecklistStatus()
  }, [fetchChecklistStatus])

  return {
    checklistStatus,
    isLoading,
    error,
    refetch,
    shouldShowChecklist
  }
}
